import { Controller } from "@hotwired/stimulus"
import noUiSlider from "nouislider"
import { CurrencyHelper } from "~/helpers/currency_helper"
import { FormHelper } from "~/helpers/form_helper"

export default class extends Controller {
  static targets = [
    "creditsSlider", "relatedInput", "creditsInput", "creditsPrice",
    "buyBtn", "form", "currentCard", "newCard", "cardField",
    "fullName", "invoiceId", "paymentIntentId"
  ]

  static values = {
    currency: String,
    minCredits: Number,
    maxCredits: Number,
    sliderStep: Number,
    stripeKey: String,
    pricePerPack: Number
  }

  connect() {
    this.initializeSlider()
    this.initializeCardFormVisibility()
    this.checkButtonState()
    this.resizeInput({ target: this.creditsInputTarget })

    this.creditsInputTarget.addEventListener("input", this.resizeInput)
  }

  initialize() {
    var _this = this

    if (typeof App !== 'undefined' && App.whenAvailable) {
      App.whenAvailable("Stripe", function () {
        _this.initializeStripe()
        _this.loadStripeElements()
      })
    } else if (typeof Stripe !== 'undefined') {
      this.initializeStripe()
      this.loadStripeElements()
    }
  }

  initializeCardFormVisibility() {
    // Check if user has an existing card that should be shown by default
    const hasVisibleCurrentCard = this.hasCurrentCardTarget && $(this.currentCardTarget).is(':visible')
    const hasVisibleNewCard = this.hasNewCardTarget && $(this.newCardTarget).is(':visible')

    // If neither form is visible, show the appropriate default
    if (!hasVisibleCurrentCard && !hasVisibleNewCard) {
      if (this.hasCurrentCardTarget) {
        $(this.currentCardTarget).show()
      } else if (this.hasNewCardTarget) {
        $(this.newCardTarget).show()
      }
    }
  }

  initializeSlider() {
    const relatedInput = this.relatedInputTarget
    const creditsInput = this.creditsInputTarget
    const creditsPrice = this.creditsPriceTargets
    const _this = this

    // The slider UI works with display credits, so we must convert the step value.
    // The conversion factor is 10 (from Billing::UNIFIED_CREDITS_BASE).
    const displayCreditsPer10Dollars = this.sliderStepValue / 10.0
    const initialValue = parseInt(relatedInput.value, 10) || this.minCreditsValue

    noUiSlider.create(this.creditsSliderTarget, {
      start: [initialValue],
      step: displayCreditsPer10Dollars,
      connect: [true, false],
      format: {
        to: (v) => parseFloat(v).toFixed(0),
        from: (v) => parseFloat(v).toFixed(0)
      },
      range: {
        'min': [this.minCreditsValue],
        'max': [this.maxCreditsValue]
      }
    })

    this.creditsSliderTarget.noUiSlider.on("update", (values, handle) => {
      let sliderValue = parseInt(values[handle], 10) // This value is in display credits

      // Update the input value and credits count display
      relatedInput.value = sliderValue
      creditsInput.value = App.numberWithCommas(sliderValue)
      _this.resizeInput({ target: creditsInput })

      // Calculate price based on consistent units (display credits).
      // Determine how many $10 units are needed to purchase the selected 'sliderValue' of display credits.
      const units = Math.ceil(sliderValue / displayCreditsPer10Dollars)
      const price = units * this.pricePerPackValue
      creditsPrice.forEach(el => el.textContent = CurrencyHelper.numberToCurrency(price, this.currencyValue))
    })
  }

  updateSliderValue(event) {
    const value = Number(event.target.value.replace(/,/g, ''))
    this.creditsSliderTarget.noUiSlider.set(value)
  }

  initializeStripe() {
    if (this.stripe) {
      return
    }

    if (this.hasStripeKeyValue && typeof Stripe !== 'undefined') {
      this.stripe = Stripe(this.stripeKeyValue)
    }
  }

  resizeInput(event) {
    const input = event.target
    input.style.width = (input.value.length + 1 || 3) + "ch"
  }

  loadStripeElements() {
    if (!this.stripe) return
    if (!this.hasCardFieldTarget) return

    this.card = this.stripe.elements().create("card", FormHelper.elementStyles("13px"))
    this.card.mount(this.cardFieldTarget)
    this.card.on('ready', () => {
      setTimeout(() => {
        this.checkButtonState()
      }, 100)
    })
  }

  showNewCardForm() {
    if (this.hasCurrentCardTarget) $(this.currentCardTarget).hide()
    if (this.hasNewCardTarget) $(this.newCardTarget).show()
    this.checkButtonState()
  }

  hideNewCardForm() {
    if (this.hasNewCardTarget) $(this.newCardTarget).hide()
    if (this.hasCurrentCardTarget) $(this.currentCardTarget).show()
    this.checkButtonState()
  }

  checkButtonState() {
    if (!this.hasBuyBtnTarget) return

    const hasExistingCard = this.hasCurrentCardTarget && $(this.currentCardTarget).is(':visible')

    if (hasExistingCard) {
      this.buyBtnTarget.disabled = false
      return
    }

    const newCardReady = this.hasNewCardTarget && $(this.newCardTarget).is(':visible') && this.card
    this.buyBtnTarget.disabled = !newCardReady
  }

  submit(e) {
    if (typeof e != "undefined") FormHelper.disableForm(e.target)

    if (this.hasNewCardTarget && $(this.newCardTarget).is(":visible")) {
      this.createPaymentMethodAndSubmit()
    } else {
      this.submitForm()
    }
  }

  createPaymentMethodAndSubmit() {
    if (!this.stripe || !this.card) {
      FormHelper.displayFlashError("Payment system not initialized")
      FormHelper.enableForm()
      return
    }

    const billingDetails = { name: this.fullNameTarget.value, email: gon.user.email }

    this.stripe.createPaymentMethod({
      type: "card",
      card: this.card,
      billing_details: billingDetails
    }).then(result => {
      if (result.error) {
        FormHelper.displayFlashError(result.error.message)
        FormHelper.enableForm()
        return
      }
      this.savePaymentMethodAndSubmit(result.paymentMethod.id)
    })
  }

  savePaymentMethodAndSubmit(paymentMethodId) {
    $.ajax({
      url: "/credit-cards",
      type: "POST",
      dataType: "json",
      data: { payment_method_id: paymentMethodId },
      headers: { "X-CSRF-Token": $("meta[name='csrf-token']").attr("content") },
      success: () => {
        this.submitForm()
      },
      error: (jqXHR) => {
        const errorMsg = jqXHR.responseJSON ? jqXHR.responseJSON.error : i18n.t("js.back.checkout.errors.saving_credit_card")
        FormHelper.displayFlashError(errorMsg)
        FormHelper.enableForm()
      }
    })
  }

  async submitForm() {
    try {
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        body: new FormData(this.formTarget),
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
          "Accept": "application/json, text/vnd.turbo-stream.html"
        }
      })

      const contentType = response.headers.get("content-type")
      if (contentType?.includes("application/json")) {
        const data = await response.json()
        if (data.payment_intent_secret) {
          this.attempt3DSChallenge(data.payment_intent_secret, data.invoice_id)
        } else {
          FormHelper.displayFlashError("An unexpected error occurred.")
          FormHelper.enableForm()
        }
      } else if (contentType?.includes("text/vnd.turbo-stream.html")) {
        Turbo.renderStreamMessage(await response.text())
        if (!response.ok) FormHelper.enableForm()
      }
    } catch (error) {
      FormHelper.displayFlashError("A network error occurred. Please try again.")
      FormHelper.enableForm()
    }
  }

  attempt3DSChallenge(clientSecret, invoiceId) {
    this.stripe.handleCardPayment(clientSecret).then(result => {
      if (result.error) {
        FormHelper.displayFlashError(result.error.message)
        FormHelper.enableForm()
        return
      }
      // 3DS successful, set the confirmed payment intent ID and re-submit
      this.paymentIntentIdTarget.value = result.paymentIntent.id
      this.invoiceIdTarget.value = invoiceId
      this.submitForm()
    })
  }
}
